import React, { useEffect, useMemo, useRef, useState } from "react";

import { Grid } from "@mui/material";
import { UseQueryResult } from "@tanstack/react-query";
import ShipeeziGrid, {
  Category,
  GeneralActions,
  Column,
  GridProps,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import { ReportProps } from "../..";
import { Color } from "../../../../../../../../components/Form/Button";
import { NotificationToast } from "../../../../../../../../components/Generic/Toast/NotificationToast";
import SendEmailModal, { SendEmailHandles } from "../../../../../../../../components/SendEmail";
import { Booking } from "../../../../../../../../models/booking";
import { ChainReport } from "../../../../../../../../models/chain.report";
import { Cronjob } from "../../../../../../../../models/cronjob";
import { PurchaseOrder } from "../../../../../../../../models/purchase.order";
import { SearchAttribute } from "../../../../../../../../models/search.attribute";
import { Filters, Searches } from "../../../../../../../../models/shipeezi.table";
import { ColumnWithKey } from "../../../../../../../../types";
import { paginate } from "../../../../../../../../util/paginate";
import { useActionReportMaintenance } from "../../hooks/useAction";
import { EmptyDiv } from "../../styled";
import { DateOption, FilterByOption } from "../../types";
import { getRelatedFilters } from "../../utils";
import { NoWrapButton } from "../Grids/Invoices/styles";
import { DisabledText, EmptyText, RequiredFiltersText, RightActionsGrid } from "./styled";
import {
  filterBaseGridReport,
  removeKeysFromObject,
  requiredFiltersMessage,
  transformFiltersForLocalKeys,
} from "./util";

const downloadSvg = require("Assets/img/document-download-outline.svg");
const noSearchResultSvg = require("Assets/img/noSearchResult.svg");

type ReportGridData = Booking | PurchaseOrder;
export type RequiredFiltersValidation = {
  name: string;
  message: string;
  title: string;
};

interface BaseReportGridProps
  extends Omit<Partial<GridProps>, "columns" | "generalActions" | "rowActionsExtra"> {
  getAllData: (
    page?: number | undefined,
    pageSize?: number | undefined,
    searches?: Searches | undefined,
    filters?: Filters | undefined,
    disabled?: boolean | undefined
  ) => UseQueryResult<any>;
  columns: ColumnWithKey[];
  filterByOptions?: FilterByOption[];
  filterCategories?: Category[];
  generalActions?: React.ReactChild;
  dateOptions?: DateOption[];
  periodOptions?: DateOption[];
  filters?: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters | undefined>>;
  tempFilters: Filters | undefined;
  setTempFilters: React.Dispatch<React.SetStateAction<Filters | undefined>>;
  dateFilters?: Filters | undefined;
  setDateFilters?: React.Dispatch<React.SetStateAction<Filters | undefined>>;
  reportInclude?: Object[];
  rowsKey?: string;
  countKey?: string;
  defaultFilters?: Filters;
  showDateOptions?: boolean;
  allCurrentFilters: Filters | undefined;
  requiredFilters?: RequiredFiltersValidation[];
  minFilters?: number;
  onChangeData?: (value: any) => void;
  onChangeLocalData?: (value: any) => void;
  isDataLocal?: boolean;
  keysToexcludeFromLocalFilter?: string[];
  handleGenerateReport?: (csvFields: string[]) => void;
  sendMailIncludes?: Array<any>;
  generatingReportCustom?: boolean;
  showSendMail?: boolean;
}

export const BaseReportGrid = ({
  getAllData,
  columns,
  filterByOptions,
  filterCategories,
  filters,
  setFilters,
  dateOptions,
  periodOptions,
  reportInclude,
  rowsKey = "rows",
  countKey = "count",
  dateFilters,
  setDateFilters,
  allCurrentFilters,
  requiredFilters,
  minFilters = 0,
  onChangeData,
  onChangeLocalData,
  isDataLocal,
  keysToexcludeFromLocalFilter,
  handleGenerateReport,
  sendMailIncludes = [],
  generatingReportCustom,
  showSendMail,
  ...gridProps
}: BaseReportGridProps) => {
  const { t: translate } = useTranslation();

  const { useGenerateReport, useSendMailReport } = useActionReportMaintenance();
  const { mutate: generateReport, isLoading: generatingReport } = useGenerateReport();

  const sendEmailRef = useRef<SendEmailHandles>(null);

  const history = useHistory();

  const { state } = useLocation<ReportProps>();

  const { name } = state || {};

  const [data, setData] = useState<ReportGridData[]>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [selecteds, setSelecteds] = useState<number[]>([]);
  const [csvFields, setCsvFields] = useState<string[]>(
    columns?.map((column) => column?.key || String(column?.name)) || []
  );
  const [hasBeenSubmited, setHasBeenSubmited] = useState(false);
  const [validFilterQuantity, setValidFilterQuantity] = useState(false);

  useEffect(() => {
    if (!state) history.push("/");
  }, []);

  const { mutate: sendMailReport, isLoading: isLoadingSendMail } = useSendMailReport(
    allCurrentFilters,
    csvFields,
    sendMailIncludes
  );

  const isValid = (filtersToCheck?: Filters, hideWarning?: boolean) => {
    let valid = true;

    Object.entries(filtersToCheck ?? {})?.forEach((filterToCheck: any) => {
      if (filterToCheck[1] === undefined || filterToCheck[1] === "") {
        delete filtersToCheck?.[filterToCheck[0]];
      }
    });

    if (minFilters > Object.keys(filtersToCheck ?? {})?.length) {
      valid = false;
      setValidFilterQuantity(false);
      if (!hideWarning)
        NotificationToast.error(
          `${translate("enterAtLeast")} ${minFilters} ${(minFilters > 1
            ? translate("filters")
            : translate("filter")
          ).toLowerCase()}`,
          { id: "minFilters" }
        );

      return false;
    }

    setValidFilterQuantity(true);

    const currentColumnsOnFilter = Object.keys(filtersToCheck ?? {});
    const validFilters = requiredFilters?.filter((filter) => {
      const foundFilter = filterByOptions?.find((value) => {
        return value?.column === filter?.name;
      });

      if (foundFilter?.notRequiredWhen?.length) {
        return !Object.entries(filtersToCheck || {})?.find((object) => {
          return foundFilter?.notRequiredWhen?.includes(object[0]);
        });
      }

      return true;
    });

    validFilters?.forEach((filterRequired) => {
      if (filterRequired.name === "date") {
        const dateField = filtersToCheck?.date as any[];

        if (dateField?.includes(null) || !dateField?.length) {
          valid = false;
          if (!hideWarning)
            NotificationToast.error(filterRequired.message, { id: filterRequired.name });
        }
      }
      if (filterRequired.name === "period") {
        if (!Object.keys(filtersToCheck ?? {}).includes("period")) {
          valid = true;

          return;
        }

        const dateRange = filtersToCheck?.dateRange as any[];

        if (dateRange?.includes(null) || dateRange?.length !== 2) {
          valid = false;
          if (!hideWarning)
            NotificationToast.error(filterRequired.message, { id: filterRequired.name });
        }
      }
      if (!Object.keys(filtersToCheck ?? {}).includes(filterRequired.name)) {
        valid = false;
      }
    });
    filterByOptions?.forEach((option) => {
      const haveRequiredMessage = !!option.requiredMessage;
      const isNotRequired =
        haveRequiredMessage &&
        option?.notRequiredWhen?.some((column) => currentColumnsOnFilter.includes(column));

      if (
        haveRequiredMessage &&
        !currentColumnsOnFilter?.includes(option.column) &&
        !isNotRequired
      ) {
        valid = false;
        if (!hideWarning)
          NotificationToast.error(option.requiredMessage, { id: option.requiredMessage });
      }
    });

    return valid;
  };

  const searchDisabled = useMemo(
    () => !isValid(allCurrentFilters, true),
    [allCurrentFilters, allCurrentFilters?.date]
  );

  const { data: newData, isFetching: isFetchingData } = getAllData(
    isDataLocal ? undefined : page,
    isDataLocal ? undefined : pageSize,
    [],
    isDataLocal ? dateFilters : allCurrentFilters,
    searchDisabled
  );

  useEffect(() => {
    if (searchDisabled && hasBeenSubmited) {
      setData([]);
    }
  }, [searchDisabled, hasBeenSubmited]);

  const localHandledData = useMemo(() => {
    if (!isDataLocal) return [];

    let localdata = data;
    const searchAttributes: SearchAttribute[] = [];
    const localFilters = removeKeysFromObject(
      filters,
      keysToexcludeFromLocalFilter ?? []
    ) as Filters;

    if (searchAttributes.length > 0 || !!Object.keys(localFilters ?? {}).length) {
      localdata = filterBaseGridReport(
        localdata,
        searchAttributes,
        transformFiltersForLocalKeys({
          filterCategories,
          filters: localFilters,
          keysToexcludeFromLocalFilter,
        })
      ) as ReportGridData[];
    }
    if (isDataLocal) {
      setTotalItems(localdata.length);
      onChangeLocalData?.(localdata);
    }

    return paginate(localdata, page > 0 ? page : 0, pageSize);
  }, [data, page, pageSize, filters]);

  useEffect(() => {
    if (newData && newData?.[rowsKey]) {
      setHasBeenSubmited(true);
      setData(newData[rowsKey]);
      setTotalItems(newData[countKey]);
      onChangeData?.(newData[rowsKey]);
    }
  }, [newData]);

  const handleCurrentPage = (numPage: number) => {
    setPage(numPage - 1);
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleValidate = (currentFilters: Filters) => {
    if (isValid(currentFilters)) {
      return true;
    }

    return false;
  };

  const handleGenerateReportDefault = () => {
    if (!isValid(allCurrentFilters)) return;

    const data = {
      reportName: name ?? "",
      reportGroup: state?.reportGroup?.subGroup ?? "",
      csvFields,
    };

    generateReport({
      data,
      filters: getRelatedFilters(allCurrentFilters),
      include: reportInclude,
    });
  };

  const handleClickSendEmail = () => {
    sendEmailRef.current?.open();
  };

  const renderSendByEmailReport = () => (
    <Grid item>
      <NoWrapButton customColor={Color.SECONDARY_OUTLINED} onClick={handleClickSendEmail}>
        {translate("sendReportByEmail")}
      </NoWrapButton>
    </Grid>
  );

  const donwloadReportAction = () => (
    <RightActionsGrid
      container
      item
      direction="row"
      alignItems="flex-end"
      justifyContent="flex-end"
      spacing={2}
    >
      {showSendMail ? renderSendByEmailReport() : null}
      <Grid item>
        <NoWrapButton
          disabled={!data || !data?.length}
          startIcon={<img src={downloadSvg} />}
          customColor={Color.SECONDARY}
          loading={generatingReport || generatingReportCustom}
          onClick={() => {
            if (handleGenerateReport) {
              handleGenerateReport(csvFields);
            } else {
              handleGenerateReportDefault();
            }
          }}
        >
          {translate("downloadReport")}
        </NoWrapButton>
      </Grid>
    </RightActionsGrid>
  );

  const renderEmptyDiv = () => {
    console.log(filters, validFilterQuantity);
    return (
      <EmptyDiv>
        <img src={noSearchResultSvg} alt="empty state" />
        {Object.entries(filters || {})?.length && isValid(filters) ? (
          <>
            <EmptyText>{translate("noMatchingRecordsFound")}</EmptyText>
            <DisabledText>{translate("tryAdjustingYourFilters")}</DisabledText>
          </>
        ) : (
          <EmptyText>{translate("itFeelsEmptyRightNow")}</EmptyText>
        )}
        {searchDisabled && !validFilterQuantity && !!minFilters && (
          <DisabledText>
            {`${translate("youCanSeeTheReportsAfterSelectingAtLeast")}: `}
            <RequiredFiltersText>{`${minFilters} ${(minFilters > 1
              ? translate("filters")
              : translate("filter")
            ).toLowerCase()}`}</RequiredFiltersText>
          </DisabledText>
        )}
        {searchDisabled && !!requiredFilters && requiredFilters.length > 0 && validFilterQuantity && (
          <DisabledText>
            {`${translate("youCanSeeTheReportsAfterSelectingTheFollowingFilters")}: `}
            <RequiredFiltersText>{requiredFiltersMessage(requiredFilters)}</RequiredFiltersText>
          </DisabledText>
        )}
      </EmptyDiv>
    );
  };

  const handleFilterBy = (filters: Filters) => {
    handleValidate({ ...filters, ...dateFilters });
    setFilters(filters);
  };

  const gridActions: GeneralActions = {
    orderBy: {
      left: ["handleSelectDate", "handleFilterBy"],
      right: ["donwloadReportAction"],
    },
    handleFilterBy: {
      categories: filterCategories,
      onChange: handleFilterBy,
    },
    handleSelectDate: {
      onChange: (dateFilters, dateType, period) => {
        setDateFilters?.({
          ...(dateOptions ? { dateType } : {}),
          period,
          ...dateFilters,
        } as unknown as Filters);
      },
      dateOptions,
      periodOptions,
    },
    donwloadReportAction: donwloadReportAction(),
  };

  const onSendMEmailSubmit = (cronJob: Cronjob, isCreateSendRecurrence?: boolean) => {
    const handleSendMailNotification = () => {
      if (isCreateSendRecurrence) {
        NotificationToast.success(translate("emailScheduled"));
      } else {
        NotificationToast.success(translate("emailSent"));
      }
      sendEmailRef?.current?.close();
    };
    const reportData: ChainReport = {
      ...cronJob,
      isCreateSendRecurrence: isCreateSendRecurrence ?? false,
      filters: allCurrentFilters,
    };

    sendMailReport({ data: reportData }, { onSuccess: handleSendMailNotification });
  };

  return (
    <>
      <ShipeeziGrid
        {...gridProps}
        generalActions={gridActions}
        loadingData={isFetchingData}
        emptyData={() => renderEmptyDiv()}
        data={isDataLocal ? localHandledData : data}
        onChangePageSize={handlePageSize}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        columns={columns as Column[]}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        positionActionsColumn="right"
        selecteds={selecteds}
        handleSelect={setSelecteds}
        onChangeColumns={setCsvFields}
      />
      {showSendMail ? (
        <SendEmailModal
          ref={sendEmailRef}
          onSubmit={onSendMEmailSubmit}
          isLoading={isLoadingSendMail}
        />
      ) : null}
    </>
  );
};
